import React, { useCallback, useMemo, useState, useEffect } from "react";
import { StyleSheet, Platform, Linking } from "react-native";
import { useI18n } from "../hooks/i18n-hooks";
import { useTheme } from "../hooks/use-theme";
import { PermissionStatus } from "expo-modules-core";
import { useSnackbar } from "../hooks/snackbar";
import Button from "../components/Button";
import * as Device from "expo-device";
import * as Application from "expo-application";
import * as ExpoNotifications from "expo-notifications";
import VStack from "../ui_atoms/VStack";
import Text from "./Text";
import NotificationsImage from "../assets/images/notifications-image.svg";

type NotificationsProps = {
    onFinished: (result: boolean) => void;
};

const Notifications = ({ onFinished }: NotificationsProps) => {
    const i18n = useI18n();
    const [isLoading, setIsLoading] = useState(false);

    const { showSnackbar } = useSnackbar();

    const [canAskPermissionsAgain, setCanAskPermissionsAgain] = useState(true);

    const goToAppSettings = useCallback(async () => {
        if (Platform.OS === "ios") {
            await Linking.openURL("app-settings:notifications");
        } else {
            await Linking.sendIntent("android.settings.APP_NOTIFICATION_SETTINGS", [
                {
                    key: "android.provider.extra.APP_PACKAGE",
                    value: Application.applicationId || "",
                },
            ]);
        }
    }, []);

    useEffect(() => {
        void (async () => {
            const { granted, canAskAgain } = await ExpoNotifications.getPermissionsAsync();

            if (!granted && !canAskAgain) {
                setCanAskPermissionsAgain(false);
            }
        })();
    }, []);

    const askPermissions = useCallback(async () => {
        setIsLoading(true);
        if (Device.isDevice) {
            if (!canAskPermissionsAgain) {
                onFinished(false);
            }

            const { status } = await ExpoNotifications.requestPermissionsAsync();
            if (status !== PermissionStatus.GRANTED) {
                showSnackbar({
                    text: i18n.t("screens.auth.notifications.permission_rejected"),
                    mode: "error",
                });
                setIsLoading(false);
                onFinished(false);
                return;
            }
        } else {
            //eslint-disable-next-line
            console.info("Must use physical device for Push Notifications");
        }

        showSnackbar({
            text: i18n.t("screens.auth.notifications.permission_authorized"),
            mode: "success",
        });
        setIsLoading(false);
        onFinished(true);
    }, [canAskPermissionsAgain, i18n, onFinished, showSnackbar]);

    const styles = useStyles();

    return (
        <>
            <NotificationsImage width="100%" />
            <VStack h="2/3" gap={1} style={styles.contentContainer}>
                <VStack>
                    <Text style={styles.subTitle}>
                        {i18n.t("screens.auth.notifications.sub_title")}
                    </Text>
                    <Text style={styles.description}>
                        {i18n.t("screens.auth.notifications.description")}
                    </Text>
                </VStack>
                {!canAskPermissionsAgain && (
                    <Text
                        style={styles.secondButton}
                        onPress={() => {
                            void goToAppSettings();
                        }}
                    >
                        {i18n.t("screens.auth.notifications.access_app_settings")}
                    </Text>
                )}
            </VStack>
            <VStack h={1} bottom={1}>
                <Button
                    label={i18n.t("next")}
                    onPress={askPermissions}
                    processing={isLoading}
                    size="large"
                />
            </VStack>
        </>
    );
};

const useStyles = () => {
    const { fonts, marginSize, colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                image: {
                    width: "100%",
                    height: 216,
                    objectFit: "contain",
                },
                contentContainer: {
                    flex: 1,
                    justifyContent: "space-evenly",
                },
                subTitle: {
                    ...fonts.titleMedium,
                    textAlign: "center",
                },
                description: {
                    ...fonts.bodyMedium,
                    textAlign: "center",
                    marginTop: marginSize,
                },
                secondButton: {
                    ...fonts.bodyMedium,
                    textAlign: "center",
                    color: colors.secondary,
                },
            }),
        [marginSize, fonts.titleMedium, fonts.bodyMedium, colors.secondary],
    );
};

export default Notifications;
